import { pushRoute } from '../../../lib/router.js';
import moment from 'moment';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getOrgSlug, mapFinalFormErrors } from '../../../lib/utils.js';
import {
  applyChangeInsight,
  retrieveTemplatesFeedbackForm,
} from '../../../modules/changeInsights.js';
import { retrieveInitiative } from '../../../modules/initiatives.js';
import CustomTooltip from '../../CustomTooltip.jsx';
import NotesIcon from '../../icons/NotesIcon.jsx';
import QuestionBlueIcon from '../../icons/QuestionBlueIcon.jsx';

import SelectDateForm from './SelectDateForm.jsx';
import { validateFeedbackFormDates } from './utils.js';
import { getOrgTermsContent } from '../../../content/utils.js';
import Empty from '../../Empty.jsx';
import { noTemplateContent } from '../../EmptyStates.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      retrieveInitiative,
      retrieveTemplatesFeedbackForm,
      applyChangeInsight,
    },
    dispatch
  );

const SelectChangeInsightsModal = ({
  surveyTemplates,
  showModal,
  handleClose,
  initiative,
  applyChangeInsight,
  retrieveInitiative,
  retrieveTemplatesFeedbackForm,
  organization,
}) => {
  const slug = getOrgSlug();
  const formRef = useRef(null);
  const [shownTemplates, setShownTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const selectedDate = { startDate: '', endDate: null };

  const onSelect = (template) => {
    setSelectedTemplate(template);
  };

  const onPreview = (template) => {
    window.open(
      `/${slug}/organization/custom-settings/change-insights/template/${template.id}`,
      '_blank'
    );
  };

  const onCancel = () => {
    setSelectedTemplate(null);
  };

  const onCustomTemplate = () => {
    pushRoute('change-insights/new', {
      initiativeType: initiative.type,
      initiativeId: initiative.id,
    });
  };

  const onSubmit = (values) => {
    let sendData = {};
    if (selectedTemplate) {
      sendData = {
        surveyTemplateId: selectedTemplate.id,
        title: selectedTemplate.title,
        startDate: moment(values.startDate).format('DD/MM/YYYY'),
        endDate: moment(values.endDate).format('DD/MM/YYYY'),
      };
    }

    const submitPromise = applyChangeInsight(
      initiative.id,
      initiative.type,
      sendData
    );

    return submitPromise
      .then(() => {
        retrieveInitiative(initiative.type, initiative.id);
        handleClose();
      })
      .catch(mapErrors);
  };

  useEffect(() => {
    if (surveyTemplates) {
      setShownTemplates(
        surveyTemplates.filter((template) => template.status === 'published')
      );
    }
  }, [surveyTemplates]);

  useEffect(() => {
    if (showModal) {
      retrieveTemplatesFeedbackForm();
    }
  }, [showModal]);

  return (
    <Modal
      bsSize={selectedTemplate ? 'medium' : 'large'}
      show={showModal}
      className="no-transform"
      onHide={() => {
        handleClose();
        setSelectedTemplate(null);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {!selectedTemplate ? 'Add Feedback Form' : 'Feedback Settings'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedTemplate ? (
          <div>
            <Form
              initialValues={selectedDate}
              onSubmit={onSubmit}
              component={SelectDateForm}
              validate={validateFeedbackFormDates}
              onCancel={onCancel}
              formRef={formRef}
            />
          </div>
        ) : null}
        {!selectedTemplate ? (
          <React.Fragment>
            {shownTemplates.length ? (
              <p className="text-muted m-b-24">
                Please select a feedback form template, which you can customize
                and send to your audience.
                <br />
                Don't see a feedback form you like? You can also create your
                own!
              </p>
            ) : null}
            <div>
              {shownTemplates.length ? (
                <Row
                  style={{
                    borderTop: '1px solid #DFE2E5',
                    borderBottom: '1px solid #DFE2E5',
                    padding: '10px 0px',
                    margin: '0',
                  }}
                >
                  <Col xs={9}>
                    <span className="text-muted">Title</span>
                  </Col>
                  <Col xs={3}>
                    <span className="text-muted">Actions</span>
                  </Col>
                </Row>
              ) : null}

              {shownTemplates.length ? (
                shownTemplates
                  .sort((a, b) => a.title.localeCompare(b.title))
                  .map((template, index) => (
                    <Row
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid #DFE2E5',
                        padding: '14px 0px',
                        margin: '0',
                      }}
                    >
                      <Col xs={9}>
                        <div className="flex">
                          <strong>{template.title}</strong>

                          {template.description ? (
                            <CustomTooltip
                              placement="bottom"
                              tooltip={
                                <div style={{ maxWidth: '256px' }}>
                                  <strong>
                                    <h5
                                      style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        lineHeight: '20px',
                                        color: '#153142',
                                      }}
                                      className="m-b-8"
                                    >
                                      {template.title}
                                    </h5>
                                  </strong>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: '400',
                                      lineHeight: '20px',
                                      color: '#7F8FA4',
                                    }}
                                    className="m-b-0 text-muted"
                                  >
                                    {template.description}
                                  </p>
                                </div>
                              }
                              id="tooltip"
                            >
                              <Button
                                aria-label="Feedback Form"
                                type="button"
                                className="flex flex-align-center m-l-8 btn-last-update-icon"
                              >
                                <NotesIcon />
                              </Button>
                            </CustomTooltip>
                          ) : null}
                        </div>
                      </Col>
                      <Col xs={3}>
                        <div className="flex">
                          <Button
                            className="btn btn-outlined-with-icons flex m-r-8"
                            onClick={() => onPreview(template)}
                          >
                            Preview
                          </Button>
                          <Button
                            bsStyle="primary"
                            onClick={() => onSelect(template)}
                          >
                            Select
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  ))
              ) : (
                <React.Fragment>
                  <hr />
                  <Empty className="py-0" content={noTemplateContent} />
                  <hr />
                </React.Fragment>
              )}

              <div
                style={{
                  marginTop: '32px',
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <p className="text-muted m-b-0">
                    Didn’t find relevant template? Create Feedback from the
                    scratch.
                  </p>
                  <CustomTooltip
                    placement="right"
                    tooltip={
                      <div style={{ maxWidth: '256px' }}>
                        <strong>
                          <h5
                            style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              lineHeight: '20px',
                              color: '#153142',
                            }}
                            className="m-b-8"
                          >
                            Create Feedback
                          </h5>
                        </strong>
                        <p
                          style={{
                            fontSize: '12px',
                            fontWeight: '400',
                            lineHeight: '20px',
                            color: '#7F8FA4',
                          }}
                          className="m-b-0 text-muted"
                        >
                          Please note this form won’t be available to other{' '}
                          {getOrgTermsContent('initiatives')}. If you want to
                          create a template, do that via ChangeInsights Hub in
                          Custom Settings.
                        </p>
                      </div>
                    }
                    id="tooltip"
                  >
                    <Button
                      aria-label="View Support for Change modal"
                      type="button"
                      className="flex flex-align-center m-l-8 btn-last-update-icon"
                    >
                      <QuestionBlueIcon />
                    </Button>
                  </CustomTooltip>
                </div>
                <Button
                  block
                  bsStyle="primary"
                  style={{ maxWidth: '186px' }}
                  onClick={onCustomTemplate}
                >
                  Create Feedback Form
                </Button>
              </div>
            </div>
          </React.Fragment>
        ) : null}
      </Modal.Body>
      <Modal.Footer />
    </Modal>
  );
};

export default connect(null, mapDispatchToProps)(SelectChangeInsightsModal);
